import React, { Fragment, FunctionComponent, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { pdfSettingsActionCreators } from '@core/redux/pdfSettings';
import { Dropdown } from '@components/shared';
import { Loader } from '@components/loaders';
import { Api, Models, States } from '@core/types';
import Checkbox from '@components/shared/Checkbox';
import { t } from '@components/translations';
import { PDFItemType, PDFSettingStep, PDFSettingStepOption, PDFType } from '@core/enums/project';
import {
  FormValue,
  Template,
  Product,
  Item,
  PDFSettingStepField,
  Field,
  FormattedNumberValue,
  ProjectProduct,
  Category,
  FormattedNumberConfig,
} from '@core/types/models';
import Textbox from '@components/shared/Textbox';
import ImageUploader from '@components/shared/ImageUploader';
import DragList from '@components/shared/DragList';
import { PDFProps } from '@core/types/api';
import FormattedNumber, { formatNumber } from '@components/shared/FormattedNumber';
import {
  currencies,
  downloadOrderDetailFields,
  PDFSettingStepOptions,
  PDFFlyerTemplateOptions,
  PDFTagTemplateOptions,
  PDFTagPrintTemplateOptions,
  allOrderDetailFields,
} from 'config';
import { ApiService, ProductService } from '@core/services';

export interface IPdfSettingsProps {
  app: States.IAppState;
  project: States.IProjectState;
  pdfSettings: States.IPdfSettingsState;
  product: States.IProductState;
  pdf: States.IPdfState;
  onClose: (() => void) | undefined;
  type: PDFType;
  handleGeneratePDF: (props: PDFProps) => void;
  isPrint?: boolean;
  setPdfItemType: typeof pdfSettingsActionCreators.setPdfItemType;
  setCurrency: typeof pdfSettingsActionCreators.setCurrency;
  setField: typeof pdfSettingsActionCreators.setField;
  handleClose: typeof pdfSettingsActionCreators.handleClose;
  handleSetCurrentProductIndex: typeof pdfSettingsActionCreators.handleSetCurrentProductIndex;
  handleSetCurrentStep: typeof pdfSettingsActionCreators.handleSetCurrentStep;
  handleSetFlyerTemplate: typeof pdfSettingsActionCreators.handleSetFlyerTemplate;
  handleSetRequiresReview: typeof pdfSettingsActionCreators.handleSetRequiresReview;
  handlePdfItemTypeChange: typeof pdfSettingsActionCreators.handlePdfItemTypeChange;
  onSubmit?: () => void;
}

const PdfSettings: FunctionComponent<IPdfSettingsProps> = ({
  project,
  app,
  pdfSettings,
  product,
  pdf,
  onClose,
  type,
  handleGeneratePDF,
  handleSetCurrentProductIndex,
  handleSetCurrentStep,
  handleSetFlyerTemplate,
  handleSetRequiresReview,
  handlePdfItemTypeChange,
  onSubmit,
  setPdfItemType,
  setCurrency,
  setField,
  handleClose,
  isPrint,
}) => {
  const { requiresReview, orderVisited, templates, template, currentProductIndex, settingSteps, currentStep } = pdfSettings.journeyPDF;

  const formInnerRef = useRef<HTMLDivElement>(null);

  const detailFields = isPrint ? allOrderDetailFields : downloadOrderDetailFields;

  const onItemTypeChange = (itemType: PDFItemType) => {
    handlePdfItemTypeChange(
      itemType,
      isPrint || false,
      type,
      t,
      PDFFlyerTemplateOptions,
      PDFTagTemplateOptions,
      PDFTagPrintTemplateOptions,
      PDFSettingStepOptions,
    );
    setPdfItemType(itemType);
  };

  const mapProducts = (products: ProjectProduct[], categories: Category[]): Product[] => {
    return products.flatMap((x) => {
      const productInfo = ProductService.flatten(categories).find((y) => y.variantAgilityID === x.productID);
      return productInfo
        ? Array.from({ length: x.quantity }, (_, i) => {
            return {
              ...productInfo,
              id: `${x.productID}-${i}`,
              formData: {},
            };
          })
        : [];
    });
  };

  const [products, setFlyerProducts] = useState<Product[]>(
    project.currentProject ? mapProducts(project.currentProject.products, product.currentCategories) : [],
  );

  const updateOrder = (updatedProducts: Product[]) => {
    setFlyerProducts(updatedProducts);
    handleSetCurrentProductIndex(0);
  };

  const updateProducts = React.useCallback(
    (newProducts: Product[]) => {
      // Remove products that are no longer in the project
      setFlyerProducts((prevProducts) => prevProducts.filter((p) => !newProducts.some((np) => np.id === p.id)));
      // Add new products to the end of the list
      setFlyerProducts((prevProducts) => [...prevProducts, ...newProducts]);
      handleSetCurrentProductIndex(0);
    },
    [handleSetCurrentProductIndex],
  );

  const currentProjectProducts = project.currentProject?.products;

  useEffect(() => {
    if (project.currentProject) {
      updateProducts(mapProducts(project.currentProject.products, product.currentCategories));
    } else {
      setFlyerProducts([]);
    }
  }, [currentProjectProducts, product.currentCategories, project.currentProject, updateProducts]);

  const handleSubmitInternal = () => {
    if (pdfSettings.journeyPDF.itemType === null || template === null || !settingStepComplete(settingSteps.length - 1)) {
      return;
    }
    handleGeneratePDF({
      type,
      itemType: pdfSettings.journeyPDF.itemType,
      template: template,
      formData: pdfSettings.journeyPDF.formData,
      products: products.slice(0, Math.min(products.length, template.products)),
    });
    return;
  };

  const settingStepComplete = (step: PDFSettingStep, productIndex?: number) => {
    if (template === null) {
      return false;
    }

    const requiredFields = template.fields.filter((x) => x.step <= step).filter((x) => x.required);

    // Validate titleImageUpload
    const titleImageUploadIndex = requiredFields.findIndex((x) => x.name === 'titleImageUpload');
    if (titleImageUploadIndex !== -1) {
      requiredFields.splice(titleImageUploadIndex, 1);
      if (pdfSettings.journeyPDF.formData['titleImage'] === 'upload' && !pdfSettings.journeyPDF.formData['titleImageUpload']) {
        return false;
      }
    }

    // Validate orderProducts
    const orderProductsIndex = requiredFields.findIndex((x) => x.type === 'orderProducts');
    if (orderProductsIndex !== -1) {
      requiredFields.splice(orderProductsIndex, 1);
      if (!orderVisited) {
        return false;
      }
    }

    // Validate products
    const productsIndex = requiredFields.findIndex((x) => x.type === 'products');
    if (productsIndex !== -1 && template.productFields) {
      requiredFields.splice(productsIndex, 1);

      const requiredProductsFields = template.productFields.filter((x) => x.required);

      const requiredProducts = products.slice(
        0,
        productIndex === undefined ? Math.min(products.length, template.products) : productIndex + 1,
      );

      for (const flyerProduct of requiredProducts) {
        if (!flyerProduct.formData) {
          return false;
        }

        if (
          !flyerProduct.formData ||
          requiredProductsFields.some((x) => flyerProduct.formData[x.name] === undefined || flyerProduct.formData![x.name] === '')
        ) {
          return false;
        }
      }
    }

    if (step >= PDFSettingStep.DOWNLOAD_DETAILS) {
      const requiredDetailFields = detailFields.filter((x) => x.required);

      if (
        requiredDetailFields.some(
          (x) => pdfSettings.journeyPDF.formData[x.name] === undefined || pdfSettings.journeyPDF.formData[x.name] === '',
        )
      ) {
        return false;
      }
    }

    // Validate other fields
    if (
      requiredFields.some(
        (x) => pdfSettings.journeyPDF.formData[x.name] === undefined || pdfSettings.journeyPDF.formData[x.name] === '',
      )
    ) {
      return false;
    }

    return true;
  };

  const setCurrencyInternal = (currency: FormattedNumberConfig) => {
    var updatedFlyerProducts = [...products];

    updatedFlyerProducts?.forEach((product) => {
      const standardPrice = product.formData.standardPrice as FormattedNumberValue;
      const campaignPrice = product.formData.campaignPrice as FormattedNumberValue;

      if (standardPrice?.rawValue) {
        standardPrice.formattedValue = formatNumber(standardPrice.rawValue, currency);
      }

      if (campaignPrice?.rawValue) {
        campaignPrice.formattedValue = formatNumber(campaignPrice.rawValue, currency);
      }
    });

    setCurrency(currency);

    setFlyerProducts(updatedFlyerProducts);
  };

  const updateFieldValuesBasedOnCultureCode = async (cultureCode: string) => {
    const fieldsWithRes = template?.fields.filter((x) => x.valueRes);
    const keys = fieldsWithRes?.map((x) => x.valueRes);

    if (!fieldsWithRes || !keys || keys.length === 0) {
      return;
    }

    const response = await ApiService.rawRequest<Api.IFetchTranslationsResponse>({
      baseUrl: process.env.REACT_APP_MAIN_SITE,
      method: Api.Method.POST,
      controller: 'localisation',
      slug: 'localisations',
      params: {
        cultureCode,
        keys,
      },
    });

    if (response.Localisations) {
      fieldsWithRes.forEach((field) => {
        if (field.valueRes && response.Localisations[field.valueRes]) {
          setField(field.name, response.Localisations[field.valueRes]);
        }
      });
    }
  };

  const handlePricingChanges = (updatedFormData: Record<string, FormValue>, changedField: string): Record<string, FormValue> => {
    const standardPrice = updatedFormData.standardPrice as FormattedNumberValue;
    const percentagePriceDifference = updatedFormData.percentagePriceDifference as FormattedNumberValue;
    const campaignPrice = updatedFormData.campaignPrice as FormattedNumberValue;

    if (changedField === 'standardPrice' || changedField === 'percentagePriceDifference') {
      // Calculate campaign price from standard price and percentage
      if (standardPrice?.rawValue && percentagePriceDifference?.rawValue) {
        const calculatedCampaignPrice = standardPrice.rawValue - (standardPrice.rawValue * percentagePriceDifference.rawValue) / 100;
        updatedFormData.campaignPrice = {
          rawValue: calculatedCampaignPrice,
          formattedValue: formatNumber(calculatedCampaignPrice, pdfSettings.journeyPDF.currency),
        };
      }
    } else if (changedField === 'campaignPrice') {
      // Calculate percentage from standard price and campaign price
      if (standardPrice?.rawValue && campaignPrice?.rawValue && standardPrice.rawValue > 0) {
        const calculatedPercentage = ((standardPrice.rawValue - campaignPrice.rawValue) / standardPrice.rawValue) * 100;
        const clampedPercentage = Math.max(0, Math.round(calculatedPercentage));

        updatedFormData.percentagePriceDifference = {
          rawValue: clampedPercentage,
          formattedValue: `${clampedPercentage}%`,
        };
      }
    }

    return updatedFormData;
  };

  const dropdownCultures = app.cultures
    .map((x) => ({
      key: x.cultureCode,
      value: t(`Lang_${x.cultureCode}`),
    }))
    .sort((a, b) => (a.value > b.value ? 1 : b.value > a.value ? -1 : 0));

  const renderField = (
    fields: Record<string, FormValue>,
    field: PDFSettingStepField | Field,
    setField: (key: string, value: FormValue) => void,
    groupName: string,
  ): JSX.Element | null => {
    if (field.hidden || template === null || templates === null) {
      return null;
    }

    const id = `${groupName}-${field.name}`;

    const formattedLabel = field.label + (field.required ? '*' : '') + (field.description ? ` - ${field.description}` : '');

    switch (field.type) {
      case 'string': {
        return (
          <Textbox
            key={id}
            id={id}
            value={fields[field.name] as string}
            placeholder={formattedLabel}
            multiLine={field.multiLine}
            onChange={(value) => setField(field.name, value)}
          />
        );
      }

      case 'file':
        if (field.name === 'titleImageUpload' && pdfSettings.journeyPDF.formData['titleImage'] !== 'upload') {
          return null;
        }
        return (
          <ImageUploader
            key={id}
            id={id}
            value={fields[field.name] as string}
            placeholder={formattedLabel}
            onChange={(value) => setField(field.name, value)}
          />
        );

      case 'select':
        let options: Item[] = [];
        let infoText: string | null = null;
        let previewImages: Models.TitleImage[] | undefined = undefined;

        switch (field.options) {
          case 'cultureCode':
            options = dropdownCultures;
            break;
          case 'template':
            const allTemplates: Template[] =
              pdfSettings.journeyPDF.itemType === PDFItemType.Flyer ? PDFFlyerTemplateOptions : PDFTagTemplateOptions;
            options = allTemplates.filter((x) => x.products <= products.length);

            if (options.length === 0) {
              infoText = 'No templates available for the current number of products.';
            } else if (allTemplates.length > options.length) {
              infoText = 'Some templates are not available for the current number of products.';
            }
            break;
          case 'countries':
            options = app.countries
              .map((x) => ({
                key: x.countryName,
                value: x.countryName,
              }))
              .sort((a, b) => (a.value > b.value ? 1 : b.value > a.value ? -1 : 0));
            break;
          case 'invoiceCountries':
            options = app.invoiceCountries
              .map((x) => ({
                key: x.countryName,
                value: x.countryName,
              }))
              .sort((a, b) => (a.value > b.value ? 1 : b.value > a.value ? -1 : 0));
            break;
          default:
            if (typeof field.options === 'object') {
              options = field.options;
            }
            break;
        }

        if (field.name === 'titleImage') {
          previewImages = app.titleImages;
        }

        return (
          <>
            <Dropdown
              key={id}
              id={id}
              placeholder={formattedLabel}
              onChange={(value: string) => {
                setField(field.name, value);
                if (field.name === 'template') {
                  handleSetFlyerTemplate(templates.find((x) => x.key === value) || templates[0], t);
                }
                if (field.name === 'cultureCode') {
                  setCurrencyInternal(currencies.find((x) => x.key === value.split('-')[1]) || currencies[0]);
                  updateFieldValuesBasedOnCultureCode(value);
                }
              }}
              value={fields[field.name] as string}
              options={options}
              previewImages={previewImages}
            />
            {infoText && <p>{infoText}</p>}
          </>
        );

      case 'boolean':
        return (
          <Checkbox
            key={id}
            id={id}
            label={formattedLabel}
            value={fields[field.name] as boolean}
            onClick={() => setField(field.name, !fields[field.name])}
          />
        );

      case 'orderProducts':
        return (
          <div key={id}>
            <p>
              Drag and drop to order products. The first <b>{template.products}</b> products will be included in the flyer.
            </p>
            <DragList items={products} onChange={(updatedProducts) => updateOrder(updatedProducts)} limit={template.products} />
          </div>
        );

      case 'products':
        return (
          <div key={id}>
            <p>
              Input some further information to{' '}
              {template.products === 1 ? (
                'the product'
              ) : (
                <>
                  product <b>{currentProductIndex + 1}</b>
                </>
              )}{' '}
              on the flyer.
            </p>
            <p className="product-info">
              <div className="product-info__content">
                <b>Title:</b>
                <p>{products[currentProductIndex].title}</p>
                <b>SKU:</b>
                <p>{products[currentProductIndex].sku}</p>
                <b>Features:</b>
                <p>
                  {products[currentProductIndex].features && (
                    <ul className="product-info__content__features-list">
                      {products[currentProductIndex].features?.map((feature, index) => (
                        <li key={`feature-${index}`}>{feature}</li>
                      ))}
                    </ul>
                  )}
                </p>
              </div>
              <img alt="presentation" src={products[currentProductIndex].heroImage} />
            </p>
            {template?.productFields &&
              renderFieldsWithLayout(
                products[currentProductIndex].formData || {},
                template.productFields,
                (key, value) => {
                  const updatedFlyerProducts = [...products];

                  // Update the field value
                  updatedFlyerProducts[currentProductIndex].formData = {
                    ...updatedFlyerProducts[currentProductIndex].formData,
                    [key]: value,
                  };

                  // Handle pricing calculations if it's a price-related field
                  if (['standardPrice', 'percentagePriceDifference', 'campaignPrice'].includes(key)) {
                    updatedFlyerProducts[currentProductIndex].formData = handlePricingChanges(
                      updatedFlyerProducts[currentProductIndex].formData,
                      key,
                    );
                  }

                  setFlyerProducts(updatedFlyerProducts);
                },
                `product-${products[currentProductIndex].id}`,
              )}
          </div>
        );

      case 'formattedNumber':
        return (
          <FormattedNumber
            key={id}
            id={id}
            value={fields[field.name] as FormattedNumberValue}
            placeholder={formattedLabel}
            onChange={(value) => {
              setField(field.name, value);
            }}
            formats={field.options as FormattedNumberConfig[]}
            {...(['standardPrice', 'campaignPrice'].includes(field.name) && {
              selectedFormat: pdfSettings.journeyPDF.currency,
              setFormat: setCurrencyInternal,
            })}
          />
        );
      default:
        return null;
    }
  };

  const renderFieldsWithLayout = (
    fields: Record<string, FormValue>,
    fieldsToRender: (PDFSettingStepField | Field)[],
    setField: (key: string, value: FormValue) => void,
    groupName: string,
  ): JSX.Element => {
    // Helper function to group consecutive half-width fields
    const groupFields = (fields: (PDFSettingStepField | Field)[]) => {
      const groups: ((PDFSettingStepField | Field) | (PDFSettingStepField | Field)[])[] = [];
      let currentHalfWidthGroup: (PDFSettingStepField | Field)[] = [];

      fields.forEach((field) => {
        if (field.layout === 'halfWidth' && !field.hidden) {
          currentHalfWidthGroup.push(field);
        } else {
          // Finalize current half-width group if it exists
          if (currentHalfWidthGroup.length > 0) {
            groups.push([...currentHalfWidthGroup]);
            currentHalfWidthGroup = [];
          }
          // Add non-half-width field
          if (!field.hidden) {
            groups.push(field);
          }
        }
      });

      if (currentHalfWidthGroup.length > 0) {
        groups.push([...currentHalfWidthGroup]);
      }

      return groups;
    };

    const renderHalfWidthGroup = (group: (PDFSettingStepField | Field)[], groupIndex: number, groupName: string) => {
      const pairs: (PDFSettingStepField | Field)[][] = [];
      for (let i = 0; i < group.length; i += 2) {
        pairs.push(group.slice(i, i + 2));
      }

      const groupNameWithIndex = `${groupName}-${groupIndex}`;

      return (
        <div className="form__fields--split" key={groupNameWithIndex}>
          {pairs.map((pair, pairIndex) => (
            <React.Fragment key={`${groupNameWithIndex}-${pairIndex}`}>
              {pair.map((field, index) => renderField(fields, field, setField, `${groupNameWithIndex}-${pairIndex}-${index}`))}
            </React.Fragment>
          ))}
        </div>
      );
    };

    const groupedFields = groupFields(fieldsToRender);

    return (
      <>
        {groupedFields.map((group, groupIndex) => {
          if (Array.isArray(group)) {
            return renderHalfWidthGroup(group, groupIndex, groupName);
          }

          if (group.layout === 'stacked') {
            return <div className="form__fields--stacked">{renderField(fields, group, setField, groupName)}</div>;
          }

          return renderField(fields, group, setField, groupName);
        })}
      </>
    );
  };

  const renderStepContent = (stepEnum: PDFSettingStep) => {
    if (template === null || templates === null) {
      return null;
    }

    const step = PDFSettingStepOptions[stepEnum];
    const fields = template.fields.filter((x) => x.step === stepEnum);

    let description = step.description;
    if (pdfSettings.journeyPDF.itemType === PDFItemType.Flyer && step.flyerDescription) {
      description = step.flyerDescription;
    } else if (isPrint && step.printDescription) {
      description = step.printDescription;
    }

    let title = step.title;
    if (isPrint && step.printTitle) {
      title = step.printTitle;
    }
    let longTitle = step.longTitle;
    if (isPrint && step.longPrintTitle) {
      longTitle = step.longPrintTitle;
    }

    const stepName = `step-${step.id}`;

    return (
      <div className="form__fields">
        <h2>{longTitle || title}</h2>
        {description && <p dangerouslySetInnerHTML={{ __html: description }} />}

        <div className={step.id === PDFSettingStep.FORMAT && template.pdf ? 'form__fields--format' : ''}>
          <div className="form__fields--container">
            {renderFieldsWithLayout(pdfSettings.journeyPDF.formData, fields, setField, stepName)}
          </div>
          {step.id === PDFSettingStep.FORMAT && template.pdf && (
            <embed
              src={`${template.pdf}#toolbar=0&navpanes=0&scrollbar=0#view=FitH`}
              data-template={template.key}
              title={template.key}
            />
          )}
          {step.id === PDFSettingStep.DOWNLOAD_DETAILS && (
            <div className="form__fields--print">
              {renderFieldsWithLayout(pdfSettings.journeyPDF.formData, detailFields, setField, stepName)}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Journey Step 5 (optional): Print Confirmation
  if (pdf.modal.showConfirmation) {
    return (
      <div className="form__inner">
        <div className="form__fields">
          <h2 className="align--center">PDF Has been sent to printers</h2>
          <button className="btn" onClick={() => onClose && onClose()}>
            OK
          </button>
        </div>
      </div>
    );
  }

  // Journey Step 4: Download / Print PDF
  if (pdf.projectPdf && !pdf.modal.isGenerating) {
    return (
      <Fragment>
        <div className="form__inner">
          <div className="form__fields">
            <h2 className="align--center">{type === PDFType.Download ? 'Download your PDF' : 'Review your PDF'}</h2>
            {type === PDFType.Download ? (
              <p className="align--center">Your PDF is ready to download. Click below to download your PDF.</p>
            ) : (
              <>
                <p className="align--center">You must view your PDF before sending to the printers</p>
                {pdfSettings.printPDF.projectPdf && (
                  <p className="align--center">
                    Click{' '}
                    <a
                      href={pdfSettings.printPDF.projectPdf.pdfUrl}
                      onClick={() => handleSetRequiresReview(false)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      here
                    </a>{' '}
                    to review your PDF
                  </p>
                )}
              </>
            )}
          </div>
        </div>
        <button
          className="btn"
          disabled={requiresReview}
          onClick={() => {
            onSubmit && onSubmit();
          }}
        >
          {type === PDFType.Download ? 'Download PDF' : 'Send to Printers'}
        </button>
      </Fragment>
    );
  }

  // Journey Step 3: Generating PDF
  if (pdf.modal.isGenerating) {
    return (
      <div className="form__inner">
        <div className="form__fields">
          <h2 className="align--center">Generating PDF</h2>
          <p className="align--center" dangerouslySetInnerHTML={{ __html: t('ftg.generatepdf.download') }}></p>
          <Loader />
        </div>
      </div>
    );
  }

  // Journey Step 2: PDF Settings
  if (pdfSettings.journeyPDF.itemType !== null) {
    if (currentStep === null || template === null) {
      return <span>Configuration issue</span>;
    }

    return (
      <div className="form__navigation-wrapper" data-wide={settingSteps.length > 1}>
        {/* PDF Settings step navigation */}
        {settingSteps.length > 1 && (
          <div className="form__navigation-wrapper__items">
            {settingSteps.map((step, index) => (
              <button
                data-active={index === currentStep}
                key={index}
                disabled={index > 0 ? !settingStepComplete(index - 1) : false}
                onClick={() => handleSetCurrentStep(index)}
              >
                {isPrint && step.printTitle ? step.printTitle : step.title}
              </button>
            ))}
          </div>
        )}

        <div className="form__navigation-wrapper__content">
          <div className="form__inner" ref={formInnerRef}>
            {renderStepContent(currentStep)}
          </div>

          <div className="form__button-wrapper">
            {/* PDF Settings step previous */}
            {currentStep === PDFSettingStep.PRODUCTS && currentProductIndex > 0 ? (
              <button className="btn btn--previous" onClick={() => handleSetCurrentProductIndex(currentProductIndex - 1)}>
                Previous Product
              </button>
            ) : currentStep > 0 ? (
              <button className="btn btn--previous" onClick={() => handleSetCurrentStep(currentStep - 1)}>
                {settingSteps[currentStep - 1].title}
              </button>
            ) : (
              <button className="btn" onClick={() => handleClose(type)}>
                Cancel
              </button>
            )}
            <br />
            {currentStep === PDFSettingStep.PRODUCTS && currentProductIndex < Math.min(products.length, template.products) - 1 ? (
              <button
                onClick={() => handleSetCurrentProductIndex(currentProductIndex + 1)}
                className="btn btn--next"
                disabled={!settingStepComplete(currentStep, currentProductIndex)}
              >
                Next Product
              </button>
            ) : currentStep < settingSteps.length - 1 ? (
              <>
                <button
                  onClick={() => handleSetCurrentStep(currentStep + 1)}
                  className="btn btn--next"
                  disabled={!settingStepComplete(currentStep, currentProductIndex)}
                >
                  {settingSteps[currentStep + 1].title}
                </button>
              </>
            ) : (
              <button onClick={handleSubmitInternal} className="btn" disabled={!settingStepComplete(currentStep, currentProductIndex)}>
                Create PDF
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Journey Step 1: Choose Item Type
  return (
    <div className="form__inner">
      <div className="form__fields">
        <h2 className="align--center">Choose Your Item Type</h2>
        <p className="align--center">Select the type of item you wish to generate.</p>
        <div className="form__button-wrapper">
          <button className="btn" onClick={() => onItemTypeChange(PDFItemType.Flyer)}>
            Flyer
          </button>
          <button className="btn" onClick={() => onItemTypeChange(PDFItemType.Tags)}>
            Tags
          </button>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: States.IRootState) => ({
  app: state.app,
  project: state.project,
  pdfSettings: state.pdfSettings,
  product: state.product,
});

const mapDispatchToProps = (dispatch: any) => ({
  setPdfItemType: (itemType: PDFItemType | null) => dispatch(pdfSettingsActionCreators.setPdfItemType(itemType)),
  setCurrency: (currency: FormattedNumberConfig) => dispatch(pdfSettingsActionCreators.setCurrency(currency)),
  setField: (field: string, value: FormValue) => dispatch(pdfSettingsActionCreators.setField(field, value)),
  handleClose: (type: PDFType) => dispatch(pdfSettingsActionCreators.handleClose(type)),
  handleSetCurrentProductIndex: (index: number) => dispatch(pdfSettingsActionCreators.handleSetCurrentProductIndex(index)),
  handleSetCurrentStep: (step: number) => dispatch(pdfSettingsActionCreators.handleSetCurrentStep(step)),
  handleSetFlyerTemplate: (template: Template | null) => dispatch(pdfSettingsActionCreators.handleSetFlyerTemplate(template, t)),
  handleSetRequiresReview: (requires: boolean) => dispatch(pdfSettingsActionCreators.handleSetRequiresReview(requires)),
  handlePdfItemTypeChange: (
    itemType: PDFItemType | null,
    isPrint: boolean,
    type: PDFType,
    t: (resourceString: string) => string,
    PDFFlyerTemplateOptions: Template[],
    PDFTagTemplateOptions: Template[],
    PDFTagPrintTemplateOptions: Template[],
    PDFSettingStepOptions: PDFSettingStepOption[],
  ) =>
    dispatch(
      pdfSettingsActionCreators.handlePdfItemTypeChange(
        itemType,
        isPrint,
        type,
        t,
        PDFFlyerTemplateOptions,
        PDFTagTemplateOptions,
        PDFTagPrintTemplateOptions,
        PDFSettingStepOptions,
      ),
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(PdfSettings);
